import type { Contact } from "../database";
import type { CompanyEntity } from "./company";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";

// ============================================================================
// RECURRING TRANSACTIONS TYPES
// ============================================================================

/**
 * Canonical QuickBooks sync statuses for a recurring transaction row. Single
 * source of truth — the cron pipeline, retry policy, filters, GraphQL mappers,
 * and MCP tools all derive from this list. `dead_letter` is terminal: the row
 * exhausted its automatic retries and needs human attention.
 */
export const RECURRING_SYNC_STATUSES = [
  "pending",
  "synced",
  "failed",
  "dead_letter",
] as const;

export type RecurringTransactionSyncStatus = (typeof RECURRING_SYNC_STATUSES)[number];

export function isRecurringSyncStatus(
  value: string | null | undefined
): value is RecurringTransactionSyncStatus {
  return RECURRING_SYNC_STATUSES.includes(value as RecurringTransactionSyncStatus);
}

/**
 * Base entity interface matching the database schema
 * This will be extended with generated GraphQL types after codegen
 */
export type RecurringTransaction = {
  id: string;
  // Nullable (Option A): a company-only recurring transaction has no contact.
  customer_id?: string;
  company_id?: string;
  // Denormalized, DB-trigger-populated, and selected by
  // `src/operations/recurring-transactions.graphql` — see the note at the top
  // of that file for why companies/contacts are deliberately not joined.
  company_name?: string;
  contact_name?: string;
  item: string; // QuickBooks item name (e.g., "Website Hosting")
  description?: string;
  /** Amount PER BILLING PERIOD (per unit). See `billing_cadence`. */
  amount: number;
  quantity: number;
  /** monthly (default) | quarterly | annual. */
  billing_cadence?: string;
  /** `YYYY-MM-DD` next billing date, quarterly/annual only. */
  next_bill_on?: string | null;
  sync_status: RecurringTransactionSyncStatus;
  last_synced_at?: string;
  last_synced_month?: string; // Format: 'YYYY-MM' (e.g., '2025-01')
  quickbooks_id?: string;
  quickbooks_realm_id?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  // Joined relations
  customer?: Contact;
  company?: CompanyEntity;
};

/**
 * GraphQL query result types (will be imported from generated types after codegen)
 * These are placeholders that will be replaced with actual generated types
 */
export type RecurringTransactionsQueryResult = any; // Will be GetRecurringTransactionsQuery after codegen
export type CreateRecurringTransactionResult = any; // Will be CreateRecurringTransactionMutation after codegen
export type UpdateRecurringTransactionResult = any; // Will be UpdateRecurringTransactionMutation after codegen
export type DeleteRecurringTransactionResult = any; // Will be DeleteRecurringTransactionMutation after codegen

/**
 * Form data interface for creating/editing recurring transactions
 */
export interface RecurringTransactionFormData {
  customer_id: string;
  item: string;
  description?: string;
  amount: number;
  quantity?: number;
}

/**
 * Filter options for querying recurring transactions
 */
export interface RecurringTransactionFilters {
  customerId?: string;
  companyId?: string;
  syncStatus?: RecurringTransactionSyncStatus;
  lastSyncedMonth?: string; // Format: 'YYYY-MM'
}
